import { supabase } from '@/integrations/supabase/client';

export interface S3UploadOptions {
  file: File;
  folder: 'recordings' | 'clips' | 'thumbnails';
  onProgress?: (progress: number) => void;
}

export interface S3Content {
  id: string;
  title: string;
  url: string;
  thumbnail_url?: string;
  duration?: number;
  created_at: string;
}

/**
 * Upload content to AWS S3 via Supabase Storage
 * This integrates with the IVS-attached S3 bucket
 */
export async function uploadToS3({ file, folder, onProgress }: S3UploadOptions): Promise<string> {
  const timestamp = Date.now();
  const fileExt = file.name.split('.').pop();
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error('User not authenticated');

  const filename = `${user.id}/${timestamp}.${fileExt}`;
  const bucket = folder === 'thumbnails' ? 'thumbnails' : 'recordings';

  // Simulate progress updates
  let currentProgress = 0;
  const progressInterval = onProgress ? setInterval(() => {
    currentProgress = Math.min(currentProgress + 10, 90);
    onProgress(currentProgress);
  }, 300) : null;

  const { error } = await supabase.storage
    .from(bucket)
    .upload(filename, file, {
      contentType: file.type,
      cacheControl: '3600',
      upsert: false,
    });

  if (progressInterval) clearInterval(progressInterval);
  if (onProgress) onProgress(100);

  if (error) throw error;

  return filename;
}

/**
 * Get signed URL for private S3 content
 */
export async function getSignedUrl(path: string, bucket: string = 'recordings'): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 3600); // 1 hour expiry

  if (error) throw error;
  return data.signedUrl;
}

/**
 * Get public URL for public S3 content
 */
export function getPublicUrl(path: string, bucket: string = 'thumbnails'): string {
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  return data.publicUrl;
}

/**
 * Delete content from S3
 */
export async function deleteFromS3(path: string, bucket: string = 'recordings'): Promise<void> {
  const { error } = await supabase.storage
    .from(bucket)
    .remove([path]);

  if (error) throw error;
}

/**
 * List user's content from S3
 */
export async function listUserContent(folder: 'recordings' | 'clips' = 'recordings'): Promise<S3Content[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: recordings, error } = await supabase
    .from('recordings')
    .select('*')
    .eq('creator_id', user.id)
    .eq('is_published', true)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return recordings.map((rec: any) => ({
    id: rec.id,
    title: rec.title,
    url: rec.video_url,
    thumbnail_url: rec.thumbnail_url,
    duration: rec.duration,
    created_at: rec.created_at,
  }));
}

/**
 * Store IVS recording metadata linked to S3
 */
export async function storeRecordingMetadata(data: {
  stream_id: string;
  title: string;
  video_url: string;
  duration: number;
  file_size: number;
  thumbnail_url?: string;
}): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: recording, error } = await supabase
    .from('recordings')
    .insert({
      ...data,
      creator_id: user.id,
      status: 'completed',
      is_published: true,
      published_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return recording.id;
}
